/**
 * Subscribes to the current user's own session-invalidation channel (pushed
 * by user-service on logout/revoke/disable, relayed by pro-api's /api/ws
 * gateway) and reacts immediately instead of waiting for a REST 401.
 * Mounted once from AuthProvider — see its doc comment for why that means
 * this runs on every host this SPA serves (pro/account/certification).
 */
import * as React from "react";
import { useAtomValue } from "jotai";
import { userPayloadAtom } from "./atoms";
import { useWebSocket } from "../useWebSocket";
import { handleSessionExpired } from "./session-expired";

const SESSION_INVALIDATED_ACTION = "session-invalidated";

export function useSessionInvalidationSocket(): void {
  const userId = useAtomValue(userPayloadAtom)?.sub ?? null;
  const { subscribe, unsubscribe } = useWebSocket();

  React.useEffect(() => {
    if (!userId) return;
    const channel = `entity:auth:${userId}`;
    const handler = (msg: { type: string; action?: unknown; [key: string]: unknown }) => {
      if (msg.action === SESSION_INVALIDATED_ACTION) {
        handleSessionExpired();
      }
    };
    subscribe([channel], handler);
    return () => unsubscribe([channel], handler);
  }, [userId, subscribe, unsubscribe]);
}
